import React, { useMemo } from "react";
import { Alert, Box, CircularProgress, Typography, useTheme } from "@mui/material";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const CHART_COLORS = [
  "primary.main",
  "secondary.main",
  "success.main",
  "warning.main",
];

const ActivityReportChart = ({
  labels = [],
  datasets = [],
  loading = false,
  error = "",
  height = 280,
  emptyMessage = "No data recorded for this period.",
}) => {
  const theme = useTheme();
  const paletteColors = CHART_COLORS.map((token) => {
    const [group, shade] = token.split(".");
    return theme.palette[group]?.[shade] || theme.palette.primary.main;
  });

  const hasData = datasets.some((dataset) =>
    (dataset.data || []).some((value) => Number(value) > 0),
  );

  const chartData = useMemo(
    () => ({
      labels,
      datasets: datasets.map((dataset, index) => ({
        label: dataset.label,
        data: dataset.data,
        backgroundColor: paletteColors[index % paletteColors.length],
        borderRadius: 4,
        borderSkipped: false,
        maxBarThickness: 32,
      })),
    }),
    [labels, datasets, paletteColors],
  );

  const chartOptions = useMemo(
    () => ({
      animation: false,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: datasets.length > 1,
          position: "bottom",
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const label = context.dataset.label || "";
              return `${label}: ${context.parsed.y}`;
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: { precision: 0, stepSize: 1 },
        },
        x: {
          grid: { display: false },
        },
      },
    }),
    [datasets.length],
  );

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (loading) {
    return (
      <Box sx={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ height, position: "relative" }}>
        <Bar data={chartData} options={chartOptions} />
      </Box>
      {!hasData ? (
        <Typography color="text.secondary" variant="body2" sx={{ mt: 2 }}>
          {emptyMessage}
        </Typography>
      ) : null}
    </>
  );
};

export default ActivityReportChart;
